import { gql } from '@apollo/client';
import { ButtonGroup } from 'reactstrap';
import { FieldArray, useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import { sortedUniq } from 'lodash';
import { nanoid } from 'nanoid';
import { useDataTransforms, useQueryWithFarmId } from '../../../utils/hooks';
import { useDataContext } from '../../../contexts/DataContext';
import { ResourceSelectionFormGroup } from '../template-components';
import { SelectorFormGroup, SquarePlusButton } from './SelectorElements';

const FarmCapexOptionsQuery = gql`
  query FarmCapexOptions($farmId: ID!) {
    capexResources {
      id
      name
    }
    rawCapexes(farmId: $farmId) {
      id
      name
    }
    farmTeamCapexTemplates(farmId: $farmId) {
      id
      name
    }
  }
`;

const TemplateCapexOptionsQuery = gql`
  query CapexOptions {
    capexResources {
      id
      name
    }
  }
`;

const TeamCapexOptionsQuery = gql`
  query TeamCapexOptions($id: ID!) {
    team(id: $id) {
      id
      capexResources {
        id
        name
      }
    }
  }
`;

const EquipmentSelector = ({ className, name }) => {
  const { isTemplate } = useDataContext();
  const { teamId } = useParams();
  const { data } = useQueryWithFarmId(
    // Don't show farm-only capexes for template crops
    isTemplate
      ? teamId
        ? TeamCapexOptionsQuery
        : TemplateCapexOptionsQuery
      : FarmCapexOptionsQuery,
    {
      includeFarmId: !isTemplate,
      variables: { id: teamId },
    },
  );
  const [optionGroups = []] = useDataTransforms(data, [
    // TODO this function should be memoized
    (d) => {
      // Contracted and rented capexes are only ever referenced by name
      // Flatten options into single group and filter duplicate names
      const options = sortedUniq(
        [
          ...(d.rawCapexes || []),
          ...(d.team?.capexResources || []),
          ...(d.farmTeamCapexTemplates || []),
          ...(d.capexResources || []),
        ]
          .map((c) => c.name)
          .sort(),
      );
      return [{ options }];
    },
  ]);

  const { setFieldValue, touched, errors, values } = useFormikContext();
  const inputValues = values[name] || [];
  const inputErrors = errors[name] || [];
  const inputTouched = touched[name] || [];
  return (
    <ResourceSelectionFormGroup>
      <div>
        <div className={`d-flex ${className}`}>
          <FieldArray name={name}>
            {({ push, remove }) => (
              <>
                <ButtonGroup
                  vertical
                  className={`${inputValues.length ? 'mr-2' : ''}`}
                >
                  {inputValues.map((capexString, i) => {
                    const equipmentId = `capex-typeahead-${nanoid()}`;
                    return (
                      <SelectorFormGroup
                        clearTextOnFocus
                        error={inputErrors[i]}
                        id={equipmentId}
                        key={equipmentId}
                        maxWidth="300px"
                        onChange={(v) => {
                          setFieldValue(`${name}[${i}]`, v[0].value);
                        }}
                        optionGroups={optionGroups}
                        placeholder={capexString || 'Equipment'}
                        removeOption={() => remove(i)}
                        selectedOption={capexString}
                        shouldShowError={inputTouched[i] && inputErrors[i]}
                      />
                    );
                  })}
                </ButtonGroup>
                <SquarePlusButton onClick={() => push('')} />
              </>
            )}
          </FieldArray>
        </div>
      </div>
    </ResourceSelectionFormGroup>
  );
};

export default EquipmentSelector;
